'use client'

import { usePathname } from 'next/navigation'
import Script from 'next/script'

import { ConsentTracker } from '@/components/ConsentTracker'
import { RootLayout } from '@/components/RootLayout'

function isPayloadRoute(pathname) {
  return (
    pathname === '/admin' ||
    pathname.startsWith('/admin/') ||
    pathname.startsWith('/payload-api') ||
    pathname.startsWith('/graphql') ||
    pathname.startsWith('/graphql-playground')
  )
}

export default function AppShell({ children }) {
  const pathname = usePathname()

  if (isPayloadRoute(pathname)) {
    return children
  }

  return (
    <>
      <RootLayout>{children}</RootLayout>
      <ConsentTracker />
      <Script id="google-tag-manager" strategy="afterInteractive">
        {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-PMF92Z5Z');`}
      </Script>

      {/* === Non-critical third-party scripts — all lazyOnload === */}

      {/* Leadsourcing Pixel */}
      <Script
        strategy="lazyOnload"
        id="leadsourcing-pixel"
        dangerouslySetInnerHTML={{
          __html: `!function(e,t,n,r){(n=e.createElement("script")).src="//visitor.leadsourcing.co/n?tid="+t,(r=e.getElementsByTagName("script")[0]).parentNode.insertBefore(n,r)}(document,"RNyhN7FBXvCSSkbcvLJ6");`,
        }}
      />

      {/* Leadsy — instant inbound lead tracking */}
      <Script
        id="vtag-ai-js"
        src="https://r2.leadsy.ai/tag.js"
        data-pid="cvu7gUsenPWZq79i"
        data-version="062024"
        strategy="lazyOnload"
        async
      />

      {/* Roam Drop-In Button — was beforeInteractive, now deferred */}
      <Script
        type="text/javascript"
        src="https://ro.am/lobbylinks/embed.js"
        strategy="lazyOnload"
        id="roam-embed"
      />

      {/* Visitor Tracking */}
      <Script
        type="text/javascript"
        src="https://app.visitortracking.com/assets/js/tracer.js"
        strategy="lazyOnload"
        id="visitor-tracking-pixel-src"
      />
      <Script type="text/javascript" strategy="lazyOnload" id="visitor-tracking-pixel-init">
        {`
          function init_tracer() {
          var tracer = new Tracer({
            websiteId : '2bef4a77-ce12-4284-98c2-42f090c09a96',
            async : true,
            debug : false
          });
        `}
      </Script>

      {/* Zero-to-Hero is a focused application flow — the site webchat bubble
          has no business there. This hides it even when the SDK was already
          loaded on a previous page (SPA navigation keeps the iframe alive). */}
      {pathname.startsWith('/zero-to-hero') && (
        <style>{`iframe[id^="webchat-floating-iframe"]{display:none !important;}`}</style>
      )}

      {/* Vendasta AI Chat — deferred until first user interaction to avoid 400KB+ JS blocking paint.
          Never loads while the visitor is on /zero-to-hero/* (focused application flow). */}
      <Script id="vendasta-chat-deferred" strategy="lazyOnload">
        {`(function(){var loaded=false;var home=window.location&&window.location.pathname==='/';function removeListeners(){window.removeEventListener('scroll',maybeLoadChat);window.removeEventListener('click',maybeLoadChat);window.removeEventListener('touchstart',maybeLoadChat);}function loadChat(){if(loaded)return;if(window.location.pathname.indexOf('/zero-to-hero')===0)return;loaded=true;removeListeners();var s=document.createElement('script');s.src='https://cdn.apigateway.co/webchat-client..prod/sdk.js';s.setAttribute('data-widget-id','1aa46368-673d-11f0-bb73-d6a1e24c99f7');s.defer=true;document.body.appendChild(s);}function maybeLoadChat(){if(home){var doc=document.documentElement;var threshold=Math.min(9000,(doc.scrollHeight||0)*0.42);if(window.scrollY<threshold)return;}loadChat();}window.addEventListener('scroll',maybeLoadChat,{passive:true});window.addEventListener('click',maybeLoadChat);window.addEventListener('touchstart',maybeLoadChat,{passive:true});setTimeout(home?maybeLoadChat:loadChat,home?30000:8000);})();`}
      </Script>

      {/* Jeeva */}
      <Script id="jeeva" strategy="lazyOnload">
        {`!function () {var jeeva = window.jeeva = window.jeeva || [];if (jeeva.invoked) return;jeeva.invoked = true;jeeva.methods = ['identify', 'collect'];jeeva.factory = function (method) {return function () {var args = Array.prototype.slice.call(arguments);args.unshift(method);jeeva.push(args);return jeeva;};};for (var i = 0; i < jeeva.methods.length; i++) {var key = jeeva.methods[i];jeeva[key] = jeeva.factory(key);}jeeva.load = function (key) {var script = document.createElement('script');script.type = 'text/javascript';script.async = true;script.src = 'https://r2d2-inbound-js-store-production.s3.us-east-1.amazonaws.com/' + key + '/jeeva.js';var first = document.getElementsByTagName('script')[0];first.parentNode.insertBefore(script, first);};jeeva.SNIPPET_VERSION = '1.0.';jeeva.load('9c10fcb7-6a5e-480b-af54-0de050d4536d');}();`}
      </Script>

      {/* Site Behavior */}
      <Script type="text/javascript" id="site-behavior" strategy="lazyOnload">
        {`(function() {
            try {
              if(window.location && window.location.search && window.location.search.indexOf('capture-sitebehaviour-heatmap') !== -1) {
                sessionStorage.setItem('capture-sitebehaviour-heatmap', '_');
              }
              var sbSiteSecret = '5ea64d53-3828-4f30-bb35-025d9c9dba86';
              window.sitebehaviourTrackingSecret = sbSiteSecret;
              var scriptElement = document.createElement('script');
              scriptElement.defer = true;
              scriptElement.id = 'site-behaviour-script-v2';
              scriptElement.src = 'https://sitebehaviour-cdn.fra1.cdn.digitaloceanspaces.com/index.min.js?sitebehaviour-secret=' + sbSiteSecret;
              document.head.appendChild(scriptElement); 
            }
            catch (e) {console.error(e)}
        })();`}
      </Script>
    </>
  )
}
